<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import FileUpload from 'vue-upload-component';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { emitter } from 'shared/helpers/mitt';
import PipelineAPI from '../../../../../../api/pipelineCards';
import { useConfig } from 'dashboard/composables/useConfig';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import { usePipelinePermissions } from '../../../composables/usePipelinePermissions';
import { findPipelineForCard } from '../../../helpers/pipelineStageResolver';
import { trustedAttachmentUrl } from '../../../helpers/attachmentUrl';
import { resolveReportingTimezone } from '../../../utils/localeFormatting';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  notes: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['itemUpdated', 'update:notes']);

const { t, locale } = useI18n();
const store = useStore();
const { isStacklab } = useConfig();
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const pipelines = computed(() => store.getters['pipelines/getPipelines'] || []);
const itemPipeline = computed(() =>
  findPipelineForCard(
    pipelines.value,
    selectedPipeline.value,
    props.item?.pipeline_id
  )
);
const { canEditCard } = usePipelinePermissions(itemPipeline);

const MAX_NOTE_LENGTH = 2000;
const MAX_NOTE_ATTACHMENT_SIZE_MB = 10;
const NOTE_ATTACHMENT_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
const PURGE_RETRY_DELAYS_MS = [0, 100, 400];
const PURGE_CONCURRENCY = 3;
const PURGE_QUEUE_STORAGE_PREFIX = 'pipeline-note-attachment-purge-queue-v2';
const LEGACY_PURGE_QUEUE_STORAGE_KEY =
  'pipeline-note-attachment-purge-queue-v1';
const PURGE_QUEUE_MAX_ENTRIES = 100;
const PURGE_QUEUE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const noteAttachmentAccept = NOTE_ATTACHMENT_TYPES.join(',');
const currentNote = ref('');
const savingNotes = ref(false);
const noteInputRef = ref(null);
const editingNoteId = ref(null);
const noteDraftBeforeEdit = ref('');
const noteDraftAttachmentsBeforeEdit = ref([]);
const noteAttachments = ref([]);
const isUploadingAttachment = ref(false);
const isConfirmingNoteDelete = ref(false);
const deleteConfirmationRef = ref(null);
let uploadController = null;
let uploadGeneration = 0;
const purgeFlushPromises = new Map();
const volatilePurgeQueues = new Map();
const storageFallbackKeys = new Set();
const terminalPurgeKeys = new Set();
const notifiedTerminalPurgeKeys = new Set();
const attachmentProvenanceById = new Map();
let noteSaveSequence = 0;
let componentUnmounted = false;
const claimedAttachmentsBySave = new Map();

const currentUser = computed(() => store.getters.getCurrentUser);
const normalizePositiveId = value => {
  const normalizedValue = String(value ?? '').trim();
  return /^[1-9]\d*$/.test(normalizedValue) ? normalizedValue : '';
};
const currentAttachmentContext = () => ({
  userId: normalizePositiveId(currentUser.value?.id),
  accountId: normalizePositiveId(store.getters.getCurrentAccountId),
  cardId: normalizePositiveId(props.item?.id),
});
const attachmentContextMatches = context => {
  const currentContext = currentAttachmentContext();
  return (
    !componentUnmounted &&
    currentContext.userId === context.userId &&
    currentContext.accountId === context.accountId &&
    currentContext.cardId === context.cardId
  );
};
const attachmentContextFor = (attachment, fallbackContext = {}) => {
  const attachmentId = normalizePositiveId(attachment?.id);
  const provenance = attachmentProvenanceById.get(attachmentId);
  return {
    userId: normalizePositiveId(provenance?.userId ?? fallbackContext.userId),
    accountId: normalizePositiveId(
      provenance?.accountId ?? fallbackContext.accountId
    ),
    cardId: normalizePositiveId(provenance?.cardId ?? fallbackContext.cardId),
  };
};
const attachmentClaimKey = (attachment, fallbackContext = {}) => {
  const { userId, accountId, cardId } = attachmentContextFor(
    attachment,
    fallbackContext
  );
  const attachmentId = normalizePositiveId(attachment?.id);
  return `${userId}:${accountId}:${cardId}:${attachmentId}`;
};
const stripAttachmentClientMetadata = attachment => {
  const persistedAttachment = { ...(attachment || {}) };
  delete persistedAttachment.origin;
  return persistedAttachment;
};
const sanitizeNoteForPersistence = note => ({
  ...note,
  attachments: (note?.attachments || []).map(stripAttachmentClientMetadata),
});
const releaseAttachmentProvenance = attachments => {
  attachments.forEach(attachment => {
    const attachmentId = normalizePositiveId(attachment?.id);
    if (attachmentId) attachmentProvenanceById.delete(attachmentId);
  });
};
const claimedAttachmentKeys = () =>
  new Set(
    [...claimedAttachmentsBySave.values()].flatMap(save =>
      save.attachments.map(attachment =>
        attachmentClaimKey(attachment, save.context)
      )
    )
  );
const unclaimedAttachments = (attachments, fallbackContext) => {
  const claimedKeys = claimedAttachmentKeys();
  return attachments.filter(
    attachment =>
      !claimedKeys.has(attachmentClaimKey(attachment, fallbackContext))
  );
};

const purgeEntryKey = entry =>
  `${entry.userId}:${entry.accountId}:${entry.cardId}:${entry.attachmentId}`;

const purgeQueueStorageKey = context => {
  const userId = normalizePositiveId(context?.userId);
  const accountId = normalizePositiveId(context?.accountId);
  return userId && accountId
    ? `${PURGE_QUEUE_STORAGE_PREFIX}-${userId}-${accountId}`
    : '';
};

const deduplicatePurgeEntries = (entries, queueContext) => {
  const expectedUserId = normalizePositiveId(queueContext?.userId);
  const expectedAccountId = normalizePositiveId(queueContext?.accountId);
  const oldestAllowedTimestamp = Date.now() - PURGE_QUEUE_TTL_MS;
  const uniqueEntries = new Map();
  (Array.isArray(entries) ? entries : []).forEach(entry => {
    const normalizedEntry = {
      userId: normalizePositiveId(entry?.userId),
      accountId: normalizePositiveId(entry?.accountId),
      cardId: normalizePositiveId(entry?.cardId),
      attachmentId: normalizePositiveId(entry?.attachmentId),
      createdAt: Number(entry?.createdAt),
    };
    if (
      !normalizedEntry.userId ||
      !normalizedEntry.accountId ||
      !normalizedEntry.cardId ||
      !normalizedEntry.attachmentId ||
      normalizedEntry.userId !== expectedUserId ||
      normalizedEntry.accountId !== expectedAccountId ||
      !Number.isFinite(normalizedEntry.createdAt) ||
      normalizedEntry.createdAt < oldestAllowedTimestamp ||
      normalizedEntry.createdAt > Date.now()
    ) {
      return;
    }
    uniqueEntries.set(purgeEntryKey(normalizedEntry), normalizedEntry);
  });
  return [...uniqueEntries.values()].slice(-PURGE_QUEUE_MAX_ENTRIES);
};

const discardLegacyPurgeQueue = () => {
  try {
    localStorage.removeItem(LEGACY_PURGE_QUEUE_STORAGE_KEY);
  } catch {
    // Storage cleanup remains best-effort when localStorage is unavailable.
  }
};

const writePurgeQueue = (entries, queueContext) => {
  const storageKey = purgeQueueStorageKey(queueContext);
  if (!storageKey) return [];

  const safeEntries = deduplicatePurgeEntries(entries, queueContext);
  volatilePurgeQueues.set(storageKey, safeEntries);
  try {
    if (safeEntries.length) {
      localStorage.setItem(storageKey, JSON.stringify(safeEntries));
    } else {
      localStorage.removeItem(storageKey);
    }
    storageFallbackKeys.delete(storageKey);
  } catch {
    // The in-memory copy keeps this session retryable if storage is blocked.
    storageFallbackKeys.add(storageKey);
  }
  return safeEntries;
};

const readPurgeQueue = queueContext => {
  const storageKey = purgeQueueStorageKey(queueContext);
  if (!storageKey) return [];
  discardLegacyPurgeQueue();

  let persistedQueue;
  try {
    persistedQueue = localStorage.getItem(storageKey);
  } catch {
    storageFallbackKeys.add(storageKey);
    return [...(volatilePurgeQueues.get(storageKey) || [])];
  }
  if (persistedQueue === null) {
    if (storageFallbackKeys.has(storageKey)) {
      return [...(volatilePurgeQueues.get(storageKey) || [])];
    }
    volatilePurgeQueues.delete(storageKey);
    return [];
  }

  try {
    const parsedQueue = JSON.parse(persistedQueue);
    if (!Array.isArray(parsedQueue)) {
      localStorage.removeItem(storageKey);
      volatilePurgeQueues.delete(storageKey);
      storageFallbackKeys.delete(storageKey);
      return [];
    }
    return writePurgeQueue(parsedQueue, queueContext);
  } catch {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // Ignore a second storage failure while discarding malformed data.
    }
    volatilePurgeQueues.delete(storageKey);
    storageFallbackKeys.delete(storageKey);
    return [];
  }
};

const queueAttachmentsForPurge = (
  cardId,
  attachments,
  accountId = store.getters.getCurrentAccountId,
  userId = currentUser.value?.id
) => {
  const queueContext = {
    userId: normalizePositiveId(userId),
    accountId: normalizePositiveId(accountId),
  };
  const normalizedCardId = normalizePositiveId(cardId);
  if (
    !queueContext.userId ||
    !queueContext.accountId ||
    !normalizedCardId ||
    !attachments.length
  ) {
    return [];
  }
  const newEntries = attachments
    .map(attachment => ({
      ...queueContext,
      cardId: normalizedCardId,
      attachmentId: normalizePositiveId(attachment?.id),
      createdAt: Date.now(),
    }))
    .filter(entry => entry.attachmentId);
  const persistedEntries = writePurgeQueue(
    [...readPurgeQueue(queueContext), ...newEntries],
    queueContext
  );
  const persistedKeys = new Set(persistedEntries.map(purgeEntryKey));
  return newEntries.filter(entry => persistedKeys.has(purgeEntryKey(entry)));
};

const waitForPurgeRetry = delay =>
  delay
    ? new Promise(resolve => {
        setTimeout(resolve, delay);
      })
    : Promise.resolve();

const purgeAlreadyCompleted = error =>
  [404, 410].includes(Number(error?.response?.status || error?.status));

const purgeFailureIsRetryable = error => {
  const status = Number(error?.response?.status || error?.status);
  return (
    !Number.isFinite(status) ||
    status === 0 ||
    status === 408 ||
    status === 429 ||
    status >= 500
  );
};

const deleteQueuedAttachment = async (entry, attempt = 0) => {
  if (attempt >= PURGE_RETRY_DELAYS_MS.length) return 'retryable';

  await waitForPurgeRetry(PURGE_RETRY_DELAYS_MS[attempt]);
  try {
    await PipelineAPI.deleteNoteAttachment(entry.cardId, entry.attachmentId, {
      accountId: entry.accountId,
    });
    return 'purged';
  } catch (error) {
    // Destroy is idempotent for the durable client queue: a missing/gone
    // attachment means the desired final state was already reached.
    if (purgeAlreadyCompleted(error)) return 'purged';
    if (!purgeFailureIsRetryable(error)) return 'terminal';
    return deleteQueuedAttachment(entry, attempt + 1);
  }
};

const mapWithConcurrency = async (items, limit, worker) => {
  const results = new Array(items.length);
  let nextIndex = 0;
  const runWorker = async () => {
    const index = nextIndex;
    nextIndex += 1;
    if (index >= items.length) return;
    results[index] = await worker(items[index]);
    await runWorker();
  };
  const workerCount = Math.min(limit, items.length);
  await Promise.all(Array.from({ length: workerCount }, runWorker));
  return results;
};

const flushPendingPurgeQueue = async (
  queueContext = currentAttachmentContext()
) => {
  const storageKey = purgeQueueStorageKey(queueContext);
  if (!storageKey) return new Set();
  if (purgeFlushPromises.has(storageKey)) {
    return purgeFlushPromises.get(storageKey);
  }

  const entries = readPurgeQueue(queueContext);
  if (!entries.length) return new Set();

  const flushPromise = (async () => {
    const results = await mapWithConcurrency(
      entries,
      PURGE_CONCURRENCY,
      async entry => ({
        entry,
        outcome: terminalPurgeKeys.has(purgeEntryKey(entry))
          ? 'terminal'
          : await deleteQueuedAttachment(entry),
      })
    );
    const purgedKeys = new Set(
      results
        .filter(result => result.outcome === 'purged')
        .map(result => purgeEntryKey(result.entry))
    );
    const newTerminalKeys = results
      .filter(result => result.outcome === 'terminal')
      .map(result => purgeEntryKey(result.entry));
    newTerminalKeys.forEach(key => terminalPurgeKeys.add(key));
    const completedKeys = new Set([...purgedKeys, ...newTerminalKeys]);
    if (completedKeys.size) {
      writePurgeQueue(
        readPurgeQueue(queueContext).filter(
          entry => !completedKeys.has(purgeEntryKey(entry))
        ),
        queueContext
      );
      const currentContext = currentAttachmentContext();
      const purgedAttachmentIds = new Set(
        results
          .filter(
            result =>
              result.outcome === 'purged' &&
              result.entry.userId === currentContext.userId &&
              result.entry.accountId === currentContext.accountId &&
              result.entry.cardId === currentContext.cardId
          )
          .map(result => result.entry.attachmentId)
      );
      if (purgedAttachmentIds.size) {
        noteAttachments.value = noteAttachments.value.filter(
          attachment => !purgedAttachmentIds.has(String(attachment.id))
        );
        purgedAttachmentIds.forEach(attachmentId =>
          attachmentProvenanceById.delete(attachmentId)
        );
      }
    }
    return purgedKeys;
  })();

  purgeFlushPromises.set(storageKey, flushPromise);
  try {
    return await flushPromise;
  } finally {
    if (purgeFlushPromises.get(storageKey) === flushPromise) {
      purgeFlushPromises.delete(storageKey);
    }
  }
};

const notifyTerminalPurgeFailure = keys => {
  const unnotifiedKeys = keys.filter(
    key => !notifiedTerminalPurgeKeys.has(key)
  );
  if (!unnotifiedKeys.length) return;
  unnotifiedKeys.forEach(key => notifiedTerminalPurgeKeys.add(key));
  emitter.emit('newToastMessage', {
    message: t('PIPELINE.MESSAGES.ATTACHMENT_DELETE_ERROR'),
    action: { type: 'error' },
  });
};

const purgeAttachments = async (
  cardId,
  attachments,
  {
    accountId = store.getters.getCurrentAccountId,
    userId = currentUser.value?.id,
    notifyOnFailure = true,
  } = {}
) => {
  const queueContext = {
    userId: normalizePositiveId(userId),
    accountId: normalizePositiveId(accountId),
  };
  const entries = queueAttachmentsForPurge(
    cardId,
    attachments,
    queueContext.accountId,
    queueContext.userId
  );
  if (!entries.length) return true;

  const targetKeys = new Set(entries.map(purgeEntryKey));
  const storageKey = purgeQueueStorageKey(queueContext);
  const flushWasInProgress = purgeFlushPromises.has(storageKey);
  await flushPendingPurgeQueue(queueContext);
  if (
    flushWasInProgress &&
    readPurgeQueue(queueContext).some(entry =>
      targetKeys.has(purgeEntryKey(entry))
    )
  ) {
    await flushPendingPurgeQueue(queueContext);
  }

  const terminalKeys = [...targetKeys].filter(key =>
    terminalPurgeKeys.has(key)
  );
  if (terminalKeys.length) {
    if (notifyOnFailure) notifyTerminalPurgeFailure(terminalKeys);
    return false;
  }

  const allPurged = !readPurgeQueue(queueContext).some(entry =>
    targetKeys.has(purgeEntryKey(entry))
  );
  if (!allPurged && notifyOnFailure) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ATTACHMENT_DELETE_ERROR'),
      action: { type: 'error' },
    });
  }
  if (allPurged) releaseAttachmentProvenance(attachments);
  return allPurged;
};

const purgeAttachmentsByProvenance = async (
  attachments,
  { fallbackContext = currentAttachmentContext(), notifyOnFailure = true } = {}
) => {
  const groups = new Map();
  attachments.forEach(attachment => {
    const { userId, accountId, cardId } = attachmentContextFor(
      attachment,
      fallbackContext
    );
    const attachmentId = normalizePositiveId(attachment?.id);
    if (!userId || !accountId || !cardId || !attachmentId) return;
    const key = `${userId}:${accountId}:${cardId}`;
    const group = groups.get(key) || {
      userId,
      accountId,
      cardId,
      attachments: [],
    };
    group.attachments.push(attachment);
    groups.set(key, group);
  });

  if (!groups.size) return true;
  const results = await mapWithConcurrency(
    [...groups.values()],
    PURGE_CONCURRENCY,
    group =>
      purgeAttachments(group.cardId, group.attachments, {
        userId: group.userId,
        accountId: group.accountId,
        notifyOnFailure,
      })
  );
  return results.every(Boolean);
};

const flushPendingPurgesBestEffort = () => {
  const queueContext = currentAttachmentContext();
  const queuedKeys = readPurgeQueue(queueContext).map(purgeEntryKey);
  flushPendingPurgeQueue(queueContext)
    .then(() => {
      notifyTerminalPurgeFailure(
        queuedKeys.filter(key => terminalPurgeKeys.has(key))
      );
    })
    .catch(() => {});
};

const abortAttachmentUpload = () => {
  uploadGeneration += 1;
  uploadController?.abort();
  uploadController = null;
  isUploadingAttachment.value = false;
};

const safeAttachmentUrl = attachment =>
  normalizePositiveId(attachment?.id) ? trustedAttachmentUrl(attachment) : '';

// Draft persistence: keep an unsent note across navigation/reload, scoped to
// the authenticated user, account and card.
// The watch on currentNote (below) saves on change and clears when emptied
// (addNote sets currentNote to '' on success, which clears the draft).
const noteDraftStorageKey = context => {
  const userId = normalizePositiveId(context?.userId);
  const accountId = normalizePositiveId(context?.accountId);
  const cardId = normalizePositiveId(context?.cardId);
  return userId && accountId && cardId
    ? `pipeline-note-draft-v2-${userId}-${accountId}-${cardId}`
    : '';
};
const discardLegacyNoteDraft = context => {
  const accountId = normalizePositiveId(context?.accountId);
  const cardId = normalizePositiveId(context?.cardId);
  if (!accountId || !cardId) return;
  try {
    // The legacy key was shared by every authenticated user in an account, so
    // it must never be read into the new user-scoped draft.
    localStorage.removeItem(`pipeline-note-draft-${accountId}-${cardId}`);
  } catch {
    // Draft cleanup is best-effort when storage is unavailable.
  }
};
const noteDraftKey = computed(() =>
  noteDraftStorageKey(currentAttachmentContext())
);

const clearNoteDraftForContext = context => {
  const storageKey = noteDraftStorageKey(context);
  if (!storageKey) return;
  try {
    localStorage.removeItem(storageKey);
    discardLegacyNoteDraft(context);
  } catch {
    // Draft cleanup is best-effort when storage is unavailable.
  }
};

const persistNoteDraft = value => {
  const storageKey = noteDraftKey.value;
  if (!storageKey) return;
  try {
    if (value) localStorage.setItem(storageKey, value);
    else localStorage.removeItem(storageKey);
  } catch {
    // localStorage may be unavailable (private mode/quota) — drafts are best-effort.
  }
};

watch(currentNote, value => {
  // Editing an existing note must not overwrite the unrelated unsent draft.
  if (!editingNoteId.value) persistNoteDraft(value);
});

const loadNoteDraft = () => {
  const context = currentAttachmentContext();
  const storageKey = noteDraftStorageKey(context);
  if (!storageKey) {
    currentNote.value = '';
    editingNoteId.value = null;
    noteDraftBeforeEdit.value = '';
    noteDraftAttachmentsBeforeEdit.value = [];
    noteAttachments.value = [];
    return;
  }
  try {
    discardLegacyNoteDraft(context);
    const draft = localStorage.getItem(storageKey);
    currentNote.value = draft || '';
  } catch {
    // ignore storage read errors
    currentNote.value = '';
  }
  editingNoteId.value = null;
  noteDraftBeforeEdit.value = '';
  noteDraftAttachmentsBeforeEdit.value = [];
  noteAttachments.value = [];
};

watch(
  () => [
    normalizePositiveId(currentUser.value?.id),
    normalizePositiveId(store.getters.getCurrentAccountId),
    normalizePositiveId(props.item?.id),
  ],
  ([userId, accountId, cardId], previousContext = []) => {
    const [previousUserId, previousAccountId, previousCardId] = previousContext;
    const locallyOwnedAttachments = [
      ...noteAttachments.value,
      ...noteDraftAttachmentsBeforeEdit.value,
    ];
    abortAttachmentUpload();
    if (
      previousAccountId &&
      previousCardId &&
      (previousUserId !== userId ||
        previousAccountId !== accountId ||
        previousCardId !== cardId)
    ) {
      const fallbackContext = {
        userId: previousUserId,
        accountId: previousAccountId,
        cardId: previousCardId,
      };
      purgeAttachmentsByProvenance(
        unclaimedAttachments(locallyOwnedAttachments, fallbackContext),
        { fallbackContext, notifyOnFailure: false }
      ).catch(() => {});
    }
    loadNoteDraft();
    flushPendingPurgesBestEffort();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  componentUnmounted = true;
  const context = currentAttachmentContext();
  const locallyOwnedAttachments = [
    ...noteAttachments.value,
    ...noteDraftAttachmentsBeforeEdit.value,
  ];
  abortAttachmentUpload();
  purgeAttachmentsByProvenance(
    unclaimedAttachments(locallyOwnedAttachments, context),
    { fallbackContext: context, notifyOnFailure: false }
  ).catch(() => {});
  window.removeEventListener('online', flushPendingPurgesBestEffort);
});

window.addEventListener('online', flushPendingPurgesBestEffort);

const reportingTimezone = computed(() => resolveReportingTimezone(store));
const noteSubmitLabel = computed(() => {
  if (savingNotes.value) return t('PIPELINE.SAVING');
  if (editingNoteId.value) return t('PIPELINE.SAVE');
  return t('PIPELINE.ADD');
});

const getUserLocale = () => {
  return (locale.value || navigator.language || 'en-US').replace('_', '-');
};

const formatDate = date => {
  if (!date) return '';
  try {
    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return '';
    return new Intl.DateTimeFormat(getUserLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: reportingTimezone.value,
    }).format(parsedDate);
  } catch {
    return '';
  }
};

// Build a new activity entry. Pure helper — does NOT call the backend.
// The activity is appended to item_details.activities in the same atomic
// updateCard request that persists the note (avoids the race condition
// where a second updateCard with stale props.item.item_details overwrites
// the note we just saved).
const buildActivity = (type, details) => ({
  id: Date.now() + Math.random(),
  type,
  details,
  created_at: new Date().toISOString(),
  user: {
    id: currentUser.value.id,
    name: currentUser.value.name,
    avatar_url: currentUser.value.avatar_url,
  },
});

const addNote = async () => {
  if (
    !canEditCard.value ||
    savingNotes.value ||
    (!currentNote.value.trim() && !noteAttachments.value.length)
  ) {
    return;
  }

  const saveContext = currentAttachmentContext();
  if (!saveContext.userId || !saveContext.accountId || !saveContext.cardId) {
    return;
  }
  const wasEditing = !!editingNoteId.value;
  const editingId = editingNoteId.value;
  const savedText = currentNote.value;
  const pendingAttachments = [...noteAttachments.value];
  const savedAttachments = pendingAttachments.map(
    stripAttachmentClientMetadata
  );
  const draftToRestore = {
    text: noteDraftBeforeEdit.value,
    attachments: [...noteDraftAttachmentsBeforeEdit.value],
  };
  noteSaveSequence += 1;
  const saveId = noteSaveSequence;
  claimedAttachmentsBySave.set(saveId, {
    context: saveContext,
    attachments: pendingAttachments,
  });

  try {
    savingNotes.value = true;

    let updatedNotes;
    if (editingId) {
      updatedNotes = props.notes.map(sourceNote => {
        const note = sanitizeNoteForPersistence(sourceNote);
        if (note.id === editingId) {
          return {
            ...note,
            text: savedText,
            attachments: [...(note.attachments || []), ...savedAttachments],
            updated_at: new Date().toISOString(),
            author: currentUser.value.name,
            author_id: currentUser.value.id,
            author_avatar: currentUser.value.avatar_url,
          };
        }
        return note;
      });
    } else {
      const newNote = {
        id: Date.now(),
        text: savedText,
        created_at: new Date().toISOString(),
        attachments: savedAttachments,
        author: currentUser.value.name,
        author_id: currentUser.value.id,
        author_avatar: currentUser.value.avatar_url,
      };
      updatedNotes = [
        ...(props.item.item_details?.notes || []).map(
          sanitizeNoteForPersistence
        ),
        newNote,
      ];
    }

    const activity = buildActivity(wasEditing ? 'note_edited' : 'note_added', {
      note_text: savedText.substring(0, 100),
      has_attachments: savedAttachments.length > 0,
    });
    const updatedActivities = [
      ...(props.item.item_details?.activities ||
        props.item.item_details?.history ||
        []),
      activity,
    ];

    // Send ONLY the delta. Backend does deep_merge from the DB row,
    // so spreading `...props.item.item_details` would resurrect stale
    // values for OTHER keys (priority, checklist, etc) that another
    // tab/handler may have just modified.
    const payload = {
      item_details: {
        notes: updatedNotes,
        activities: updatedActivities,
      },
    };

    const { data } = await PipelineAPI.updateCard(saveContext.cardId, payload, {
      accountId: saveContext.accountId,
    });
    releaseAttachmentProvenance(pendingAttachments);
    const saveIsCurrent = attachmentContextMatches(saveContext);
    if (!saveIsCurrent && !wasEditing) {
      clearNoteDraftForContext(saveContext);
    }
    if (saveIsCurrent) {
      emit('update:notes', data.item_details?.notes || updatedNotes);
      emit('itemUpdated');

      currentNote.value = wasEditing ? draftToRestore.text : '';
      noteAttachments.value = wasEditing ? draftToRestore.attachments : [];
      editingNoteId.value = null;
      noteDraftBeforeEdit.value = '';
      noteDraftAttachmentsBeforeEdit.value = [];

      emitter.emit('newToastMessage', {
        message: wasEditing
          ? t('PIPELINE.NOTES_UPDATED_SUCCESS')
          : t('PIPELINE.MESSAGES.NOTE_ADDED_SUCCESS'),
        action: { type: 'success' },
      });
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[PipelineNotes] addNote failed', error);
    if (attachmentContextMatches(saveContext)) {
      emitter.emit('newToastMessage', {
        message: t('PIPELINE.MESSAGES.NOTE_ADD_ERROR'),
        action: { type: 'error' },
      });
    } else {
      await purgeAttachmentsByProvenance(pendingAttachments, {
        fallbackContext: saveContext,
        notifyOnFailure: false,
      });
    }
  } finally {
    claimedAttachmentsBySave.delete(saveId);
    savingNotes.value = false;
  }
};

const editNote = note => {
  if (!canEditCard.value || savingNotes.value) return;
  noteDraftBeforeEdit.value = currentNote.value;
  noteDraftAttachmentsBeforeEdit.value = [...noteAttachments.value];
  editingNoteId.value = note.id;
  currentNote.value = note.text;
  noteAttachments.value = [];
  nextTick(() => {
    noteInputRef.value?.focus();
  });
};

const cancelNoteEdit = async () => {
  if (savingNotes.value) return;
  const context = currentAttachmentContext();
  const editAttachments = [...noteAttachments.value];
  await purgeAttachmentsByProvenance(editAttachments, {
    fallbackContext: context,
  });
  if (!attachmentContextMatches(context)) return;
  editingNoteId.value = null;
  currentNote.value = noteDraftBeforeEdit.value;
  noteAttachments.value = [...noteDraftAttachmentsBeforeEdit.value];
  noteDraftBeforeEdit.value = '';
  noteDraftAttachmentsBeforeEdit.value = [];
};

const snapshotNote = note => sanitizeNoteForPersistence(note);

const noteDeletionFingerprint = note =>
  JSON.stringify({
    id: note?.id,
    text: note?.text,
    updatedAt: note?.updated_at,
    attachments: (note?.attachments || []).map(attachment => ({
      id: attachment?.id,
      url:
        attachment?.url || attachment?.attachment_url || attachment?.file_url,
    })),
  });

const deleteNote = async noteId => {
  if (!canEditCard.value || savingNotes.value || isConfirmingNoteDelete.value) {
    return;
  }

  const deleteContext = currentAttachmentContext();
  if (
    !deleteContext.userId ||
    !deleteContext.accountId ||
    !deleteContext.cardId
  ) {
    return;
  }
  const notesSnapshot = props.notes.map(snapshotNote);
  const deletedNote = notesSnapshot.find(
    note => String(note.id) === String(noteId)
  );
  if (!deletedNote) return;
  const deletedNoteFingerprint = noteDeletionFingerprint(deletedNote);
  const activitiesSnapshot = [
    ...(props.item.item_details?.activities ||
      props.item.item_details?.history ||
      []),
  ];

  let confirmed = false;
  isConfirmingNoteDelete.value = true;
  try {
    confirmed = await deleteConfirmationRef.value?.showConfirmation();
  } finally {
    isConfirmingNoteDelete.value = false;
  }
  const currentNoteForDeletion = props.notes.find(
    note => String(note.id) === String(noteId)
  );
  if (
    !confirmed ||
    savingNotes.value ||
    !canEditCard.value ||
    !attachmentContextMatches(deleteContext) ||
    noteDeletionFingerprint(currentNoteForDeletion) !== deletedNoteFingerprint
  ) {
    return;
  }

  try {
    savingNotes.value = true;
    const updatedNotes = notesSnapshot.filter(
      note => String(note.id) !== String(noteId)
    );
    const activity = buildActivity('note_deleted', { note_id: noteId });
    const updatedActivities = [...activitiesSnapshot, activity];
    // Delta-only update — backend deep_merges. Avoid spreading
    // props.item.item_details (would clobber other tabs' state).
    const payload = {
      item_details: {
        notes: updatedNotes,
        activities: updatedActivities,
      },
    };
    const { data } = await PipelineAPI.updateCard(
      deleteContext.cardId,
      payload,
      { accountId: deleteContext.accountId }
    );
    if (attachmentContextMatches(deleteContext)) {
      emit('update:notes', data.item_details?.notes || updatedNotes);
      emit('itemUpdated');
    }
    await purgeAttachmentsByProvenance(deletedNote?.attachments || [], {
      fallbackContext: deleteContext,
      notifyOnFailure: attachmentContextMatches(deleteContext),
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[PipelineNotes] deleteNote failed', error);
    if (attachmentContextMatches(deleteContext)) {
      emitter.emit('newToastMessage', {
        message: t('PIPELINE.MESSAGES.NOTE_ADD_ERROR'),
        action: { type: 'error' },
      });
    }
  } finally {
    savingNotes.value = false;
  }
};

const removePendingAttachment = async attachmentId => {
  if (!canEditCard.value || savingNotes.value) return;
  const context = currentAttachmentContext();
  const attachment = noteAttachments.value.find(
    item => item.id === attachmentId
  );
  if (!attachment) return;
  const purged = await purgeAttachmentsByProvenance([attachment], {
    fallbackContext: context,
  });
  if (purged && attachmentContextMatches(context)) {
    noteAttachments.value = noteAttachments.value.filter(
      item => item.id !== attachmentId
    );
  }
};

const handleNoteAttachment = async file => {
  if (!file || !canEditCard.value || isUploadingAttachment.value) return;

  if (!props.item?.id) {
    return;
  }

  if (!checkFileSizeLimit(file, MAX_NOTE_ATTACHMENT_SIZE_MB)) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.NOTE_ATTACHMENT_TOO_LARGE'),
      action: { type: 'error' },
    });
    return;
  }
  const attachmentMimeType = file.file?.type?.trim();
  if (
    !attachmentMimeType ||
    !NOTE_ATTACHMENT_TYPES.includes(attachmentMimeType)
  ) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.NOTE_ATTACHMENT_TYPE_NOT_ALLOWED'),
      action: { type: 'error' },
    });
    return;
  }

  const uploadContext = currentAttachmentContext();
  const { userId, cardId, accountId } = uploadContext;
  if (!userId || !cardId || !accountId || !file.file) return;
  const controller = new AbortController();
  uploadGeneration += 1;
  const generation = uploadGeneration;
  uploadController = controller;
  isUploadingAttachment.value = true;

  try {
    const response = await PipelineAPI.uploadNoteAttachment(cardId, file.file, {
      accountId,
      signal: controller.signal,
    });

    const responseAttachmentId = normalizePositiveId(response?.data?.id);
    const uploadedAttachment = responseAttachmentId
      ? { id: responseAttachmentId }
      : null;
    const attachmentUrl = safeAttachmentUrl({
      id: uploadedAttachment?.id,
      url: response?.data?.attachment_url,
    });
    const uploadIsCurrent =
      !controller.signal.aborted &&
      generation === uploadGeneration &&
      attachmentContextMatches(uploadContext);

    if (!uploadIsCurrent || !uploadedAttachment || !attachmentUrl) {
      if (uploadedAttachment) {
        await purgeAttachments(cardId, [uploadedAttachment], {
          userId,
          accountId,
          notifyOnFailure: uploadIsCurrent,
        });
      }
      if (uploadIsCurrent && (!uploadedAttachment || !attachmentUrl)) {
        emitter.emit('newToastMessage', {
          message: t('PIPELINE.MESSAGES.NOTE_ADD_ERROR'),
          action: { type: 'error' },
        });
      }
      return;
    }

    const newAttachment = {
      id: uploadedAttachment.id,
      url: attachmentUrl,
      filename: file.file.name,
      fileType: attachmentMimeType,
      source: {
        type: 'note',
        id: Date.now(),
      },
    };

    attachmentProvenanceById.set(newAttachment.id, { ...uploadContext });
    noteAttachments.value = [...noteAttachments.value, newAttachment];
  } catch (error) {
    if (controller.signal.aborted || generation !== uploadGeneration) return;
    // eslint-disable-next-line no-console
    console.error('[PipelineNotes] handleNoteAttachment failed', error);
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.NOTE_ADD_ERROR'),
      action: { type: 'error' },
    });
  } finally {
    if (generation === uploadGeneration) {
      isUploadingAttachment.value = false;
      if (uploadController === controller) uploadController = null;
    }
  }
};
</script>

<template>
  <div class="space-y-4">
    <h3 class="text-heading-3 text-n-slate-12 mb-4">
      {{ t('PIPELINE.NOTES_LABEL') }}
    </h3>

    <!-- New note input -->
    <div v-if="canEditCard" class="space-y-3">
      <div
        v-if="editingNoteId"
        class="flex items-center justify-between rounded-lg bg-n-alpha-1 px-3 py-2"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('PIPELINE.NOTES_EDITING') }}
        </span>
        <button
          type="button"
          class="min-h-11 px-3 text-sm font-medium text-n-slate-11 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          :disabled="savingNotes"
          @click="cancelNoteEdit"
        >
          {{ t('PIPELINE.CANCEL') }}
        </button>
      </div>
      <textarea
        ref="noteInputRef"
        v-model="currentNote"
        data-pipeline-notes-input
        :maxlength="MAX_NOTE_LENGTH"
        class="w-full px-4 py-3 text-sm border border-n-weak bg-n-solid-3 rounded-lg focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand resize-none text-n-slate-12 placeholder-n-slate-9"
        :placeholder="t('PIPELINE.NOTES_PLACEHOLDER')"
        :aria-label="t('PIPELINE.NOTES_LABEL')"
        :disabled="savingNotes"
        rows="3"
      />

      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <span
            class="text-xs tabular-nums"
            :class="
              currentNote.length >= MAX_NOTE_LENGTH
                ? 'text-n-ruby-11 font-semibold'
                : 'text-n-slate-10'
            "
          >
            {{ `${currentNote.length}/${MAX_NOTE_LENGTH}` }}
          </span>
          <FileUpload
            v-if="isStacklab === true"
            :accept="noteAttachmentAccept"
            @input-file="handleNoteAttachment"
          >
            <button
              type="button"
              class="min-h-11 min-w-11 p-2 rounded-lg hover:bg-n-alpha-1 text-n-slate-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
              :aria-label="t('PIPELINE.ADD_ATTACHMENT')"
              :disabled="isUploadingAttachment || savingNotes"
            >
              <fluent-icon icon="attach" size="18" aria-hidden="true" />
            </button>
          </FileUpload>
        </div>

        <button
          type="button"
          class="min-h-11 px-4 py-2 text-sm font-medium text-white bg-n-brand rounded-lg hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="
            savingNotes ||
            isUploadingAttachment ||
            (!currentNote.trim() && noteAttachments.length === 0)
          "
          @click="addNote"
        >
          {{ noteSubmitLabel }}
        </button>
      </div>

      <ul
        v-if="noteAttachments.length"
        class="space-y-2"
        :aria-label="t('PIPELINE.NOTES_PENDING_ATTACHMENTS')"
      >
        <li
          v-for="attachment in noteAttachments"
          :key="attachment.id"
          class="flex items-center gap-2 rounded-lg bg-n-alpha-1 px-3 py-2"
        >
          <fluent-icon
            icon="attach"
            size="14"
            class="text-n-slate-10"
            aria-hidden="true"
          />
          <span class="min-w-0 flex-1 truncate text-sm text-n-slate-12">
            {{ attachment.filename }}
          </span>
          <button
            type="button"
            class="min-h-11 min-w-11 rounded-lg text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-ruby-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            :aria-label="t('PIPELINE.NOTES_REMOVE_PENDING_ATTACHMENT')"
            :disabled="savingNotes"
            @click="removePendingAttachment(attachment.id)"
          >
            <fluent-icon icon="dismiss" size="14" aria-hidden="true" />
          </button>
        </li>
      </ul>
    </div>

    <!-- Notes list -->
    <div class="space-y-3 mt-6 max-h-[calc(100vh-450px)] overflow-y-auto pr-2">
      <div
        v-for="note in notes"
        :key="note.id"
        class="p-4 bg-n-solid-3 rounded-lg"
      >
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <p class="text-sm text-n-slate-12 whitespace-pre-wrap">
              {{ note.text }}
            </p>
            <ul v-if="note.attachments?.length" class="mt-2 space-y-1">
              <li v-for="attachment in note.attachments" :key="attachment.id">
                <a
                  v-if="safeAttachmentUrl(attachment)"
                  :href="safeAttachmentUrl(attachment)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1 text-sm text-n-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                >
                  <fluent-icon icon="attach" size="14" aria-hidden="true" />
                  <span class="break-all">{{ attachment.filename }}</span>
                </a>
                <span
                  v-else
                  class="inline-flex items-center gap-1 text-sm text-n-slate-10"
                >
                  <fluent-icon icon="attach" size="14" aria-hidden="true" />
                  <span class="break-all">{{ attachment.filename }}</span>
                </span>
              </li>
            </ul>
            <div class="flex items-center gap-2 mt-2 text-xs text-n-slate-10">
              <span>{{ note.author }}</span>
              <span class="i-lucide-dot size-3" aria-hidden="true" />
              <span>{{ formatDate(note.created_at) }}</span>
            </div>
          </div>
          <div v-if="canEditCard" class="flex items-center gap-1">
            <button
              type="button"
              class="min-h-11 min-w-11 rounded-lg hover:bg-n-alpha-1 text-n-slate-10 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-50"
              :aria-label="t('PIPELINE.NOTES_EDIT')"
              :disabled="savingNotes || isConfirmingNoteDelete"
              @click="editNote(note)"
            >
              <fluent-icon icon="edit" size="14" aria-hidden="true" />
            </button>
            <button
              type="button"
              class="min-h-11 min-w-11 rounded-lg hover:bg-n-alpha-1 text-n-slate-10 hover:text-n-ruby-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-50"
              :aria-label="t('PIPELINE.NOTES_DELETE')"
              :disabled="savingNotes || isConfirmingNoteDelete"
              @click="deleteNote(note.id)"
            >
              <fluent-icon icon="delete" size="14" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <div v-if="notes.length === 0" class="text-center py-8">
        <fluent-icon
          icon="document"
          size="32"
          class="text-n-slate-9 mx-auto mb-2"
        />
        <p class="text-sm text-n-slate-10">
          {{ t('PIPELINE.NOTES_EMPTY') }}
        </p>
      </div>
    </div>

    <ConfirmationModal
      ref="deleteConfirmationRef"
      :title="t('PIPELINE.NOTES_DELETE')"
      :description="t('PIPELINE.NOTES_DELETE_CONFIRM')"
      :confirm-label="t('PIPELINE.DELETE')"
      :cancel-label="t('PIPELINE.CANCEL')"
    />
  </div>
</template>
